import React, { useState, useEffect } from "react";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Radio from "@material-ui/core/Radio";
import Grid from "@material-ui/core/Grid";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormLabel from "@material-ui/core/FormLabel";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from '@material-ui/core/FormHelperText';
import GenericField from "../../../Forms/GenericField";
import { CreatableSelect } from "../../../Forms/Select";
import Typography from "@material-ui/core/Typography";
import PercentageNumber from "../../../UI/PercentageNumber";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import { CheckCircle, Edit } from "@material-ui/icons";
import { BaseTooltip } from "../../../BaseComponents";
import {useSelector} from "react-redux";

// all field components must invoke the prop:
//   onChange(value, isValid?)
// whenever the value of the field changes.
// `isValid` is optional, and should default to `true`

// XXX: This field design has a pretty significant caveat.
//      It is very difficult for the parent form to access
//      any data besides the current value of the field.

function toBase64(arr) {
  return btoa(arr.reduce((data, byte) => data + String.fromCharCode(byte), ""));
}

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: "flex",
      flexWrap: "wrap",
      "& > *": {
        margin: theme.spacing(1),
        width: theme.spacing(28),
        height: theme.spacing(28),
      },
    },
    custodianCard: {
      border: `2px solid ${theme.palette.grey[300]}`,
      cursor: "pointer",
      borderRadius: 4,
      position: "relative",
      transition: "border 0.4s ease",
      "&.selected": {
        border: `2px solid ${theme.palette.primary.main}`,
      },
      "&.disabled": {
        opacity: 0.4,
        cursor: 'not-allowed'
      },
      "& .checkIcon": {
        position: "absolute",
        top: 2,
        right: 2,
      },
    },
    custodianImage: {
      width: theme.spacing(20),
      height: theme.spacing(20),
    },
    bdRepCodeLabel: {
      "& .MuiInputBase-root.MuiInputBase-formControl": {
        marginTop: 0,
      },
    },
  })
);
export const Custodian = ({
  onChange,
  custodians,
  selectedCustodianId = undefined,
  currentHouseholdRepCode
}) => {
  const classes = useStyles();
  const [custodian, setCustodian] = useState(selectedCustodianId);

  useEffect(() => {
    if (custodian) {
      onChange(custodian);
    }
  }, [custodian]);

  const selectCustodian = (custodian) => (e) => {
    setCustodian(custodian.id);
  }

  return (
    <React.Fragment>
      <Typography>Please select the custodian for onboarding</Typography>
      <Grid container>
        {custodians.map((c) => {
          const disabled = c.requiresCustodialRepCode && currentHouseholdRepCode && !currentHouseholdRepCode.custodialRepCode;
          return (
            <Grid item className={classes.root} key={c.id}>
            <Paper
              square
              elevation={0}
              className={`
                ${classes.custodianCard} 
                ${custodian === c.id ? "selected" : ""}
                ${disabled ? "disabled" : ""}
              `}
              onClick={!disabled && selectCustodian(c)}
            >
              <Grid container direction="column">
                <Grid
                  item
                  xs={12}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  {c.logoString ? (
                    <img
                      className={classes.custodianImage}
                      src={`data:image/svg+xml;base64, ${toBase64(
                        c.logoString.data
                      )}`}
                      alt={`${c.name}`}
                    />
                  ) : (
                    c.name
                  )}
                  {custodian === c.id && (
                    <CheckCircle
                      color="primary"
                      className={"checkIcon"}
                      fontSize={"small"}
                    />
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <Typography>{c.name} </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          )}
        )}
      </Grid>

    </React.Fragment>
  );
};

export const RegistrationType = ({
  onChange,
  isERISA,
  erisaTPA,
  onboardingStrategy,
  selectedAccountType
}) => {

  const registrationTypesForERISA = erisaTPA
    ? [{name: "Non-Pershing Retirement Account", referenceKey: "Non-Pershing Retirement Account"}]
    : [
        {name: "SEP Prototype Plan", referenceKey:"SEP Prototype Plan"},
        {name: "Individual K Plan", referenceKey:"Individual K Plan"},
        {name: "SARSEP (Salary Reduction SEP)", referenceKey:"SARSEP (Salary Reduction SEP)"},
        {name: "Standard 401K Plan", referenceKey:"Standard 401K Plan"},
        {name: "Money Purchase Pension Plan", referenceKey:"Money Purchase Pension Plan"},
        {name: "Profit Sharing Plan", referenceKey:"Profit Sharing Plan"},
      ];
  const [regType, setRegType] = useState(selectedAccountType ? selectedAccountType.name : "");
  const { custodians } = useSelector(state => state.custodian);
  const selectedCustodian = custodians.find(_ => _.onboardingStrategy === onboardingStrategy);

  useEffect(() => {
    if (!!regType && regType !== '') {
      const selectedRegType = selectedCustodian.supportedAccountTypes.find(_ => _.name === regType);
      if (!!selectedRegType) {
        onChange(selectedRegType);
      }
    }
  }, [regType]);

  useEffect(() => {
    setRegType("");
    onChange("");
  }, [erisaTPA]);

  if (!selectedCustodian) {
    return null
  }
  return (
    <React.Fragment>
      <TextField
        select
        label="Registration Type"
        margin={"dense"}
        value={regType}
        onChange={(evt) => setRegType(evt.target.value)}
        helperText="Select your registration type"
      >
        {isERISA
          ? registrationTypesForERISA.map((t) => (
              <MenuItem key={t.name} value={t.name}>
                {" "}
                {t.name}{" "}
              </MenuItem>
            ))
          : selectedCustodian.supportedAccountTypes.map((t) => (
              <MenuItem key={t.name} value={t.name}>
                {" "}
                {t.name}{" "}
              </MenuItem>
            ))
          }
      </TextField>
      {regType === "Individual K Plan" && (
        <Typography style={{ fontSize: 12 }}>
          Your plan must cover only you (or you and your spouse) and you (or you
          and your spouse) must own the entire business (which may be
          incorporated, unincorporated, or LLC).
          <br />
          Your plan must cover only one or more partners (or partners and their
          spouses) in a business organized as a business partnership, and all
          partners must own at least 10% of the business.
        </Typography>
      )}
    </React.Fragment>
  );
};

export const SelectACHRequired = ({ onChange }) => {
  const [req, setReq] = useState("no");
  useEffect(() => {
    onChange(req === "yes");
  }, [req]);
  return (
    <FormControl component="fieldset" margin={"dense"}>
      <FormLabel component="legend" focused={false}>
        ACH setup required per registration ?
      </FormLabel>
      <RadioGroup row value={req} onChange={(evt) => setReq(evt.target.value)}>
        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
        <FormControlLabel value="no" control={<Radio />} label="No" />
      </RadioGroup>
      {req === "yes" && (
        <FormHelperText>
          To facilitate digital signatures for each bank signer, please add each individual as Signers in the Signature section of the ACH Form. (Before submitting for DocuSign).
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const AccountType = ({ onChange }) => {
  const [type, setType] = useState("single");
  useEffect(() => {
    onChange(type);
  }, [type]);
  return (
    <FormControl component="fieldset" margin={"dense"}>
      <FormLabel component="legend" focused={false}>
        Please select the type of account you wish to open.
      </FormLabel>
      <RadioGroup
        row
        value={type}
        onChange={(evt) => setType(evt.target.value)}
      >
        <FormControlLabel value="single" control={<Radio />} label="Single" />
        <FormControlLabel value="joint" control={<Radio />} label="Joint" />
      </RadioGroup>
    </FormControl>
  );
};

export const FundingViaTransfer = ({ onChange }) => {
  const [fvt, setFVT] = useState("no");
  useEffect(() => {
    onChange(fvt === "yes");
  }, [fvt]);
  return (
    <FormControl component="fieldset" margin={"dense"}>
      <FormLabel component="legend" focused={false}>
        Funding via transfer ?
      </FormLabel>
      <RadioGroup row value={fvt} onChange={(evt) => setFVT(evt.target.value)}>
        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
        <FormControlLabel value="no" control={<Radio />} label="No" />
      </RadioGroup>
    </FormControl>
  );
};

export const SystemicIRADistributions = ({ onChange }) => {
  const [choice, setChoice] = useState("no");
  useEffect(() => {
    onChange(choice === "yes");
  }, [choice]);
  return (
    <FormControl component="fieldset" margin={"dense"}>
      <FormLabel component="legend" focused={false}>
        Systematic IRA Distributions or Standing Tax Withholding ?
      </FormLabel>
      <RadioGroup
        row
        value={choice}
        onChange={(evt) => setChoice(evt.target.value)}
      >
        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
        <FormControlLabel value="no" control={<Radio />} label="No" />
      </RadioGroup>
    </FormControl>
  );
};

export const AccountOwners = ({
  householdMembers,
  setAccountOwner,
  allowBoth,
  setTrustees,
}) => {
  const [choice, setChoice] = useState("0");
  useEffect(() => {
    if (allowBoth) {
      setTrustees(choice);
    } else {
      setAccountOwner(choice === "0");
    }
  }, [choice]);
  if (!householdMembers[1]) {
    return null;
  }
  return (
    <FormControl component="fieldset" margin={"dense"}>
      <FormLabel component="legend" focused={false}>
        Which household member is the {allowBoth ? 'trustee' : 'account holder'}?
      </FormLabel>
      <RadioGroup
        row
        value={choice}
        onChange={(evt) => setChoice(evt.target.value)}
      >
        {householdMembers.map((member, index) => (
          <FormControlLabel
            key={index}
            value={index.toString()}
            control={<Radio />}
            label={`${member.firstName} ${member.lastName}`}
          />
        ))}
        {allowBoth && (
          <FormControlLabel value={"2"} control={<Radio />} label={`Both`} />
        )}
      </RadioGroup>
    </FormControl>
  );
};

export const Trusts = ({ onChange, trustOptions = [] }) => {
  const [trust, setTrust] = useState("");
  useEffect(() => {
    onChange(trust);
  }, [trust]);

  return (
    <GenericField
      margin={"normal"}
      required
      onChange={setTrust}
      inputComponent={CreatableSelect}
      label="What's the name of the trust?"
      inputProps={{
        value: trust,
        // defaultValue: { label: trust, value: trust },
        formatCreateLabel: (inputValue) => `Use "${inputValue}"`,
        isClearable: true,
        options:
          trustOptions.map((opt) => {
            return { label: opt, value: opt };
          }) || [],
        placeholder: "",
      }}
    />
  );
};

export const AdvisorFee = ({ onClick, advisorFee }) => {
  return (
    <FormControl component="fieldset" margin={"dense"}>
      <FormLabel component="legend">Advisor Fee</FormLabel>
      <Typography>
        <PercentageNumber value={advisorFee} needCalculation={false} />
        <Button size={"small"} onClick={onClick} color={"secondary"}>
          <Edit fontSize={"small"} /> Edit portfolio
        </Button>
      </Typography>
    </FormControl>
  );
};

export const BDRepCode = ({
  onChange,
  bdRepCodes,
  custodians,
  selectedCustodianId,
  selectedBdRepCodeId = null,
  currentHouseholdRepCode,
}) => {
  const [bdRepCodeId, setBdRepCodeId] = useState(selectedBdRepCodeId);
  const [filteredRepCodes, setFilteredRepCodes] = useState(bdRepCodes);
  const selectedCustodian = custodians.find(c => c.id === selectedCustodianId);
  const classes = useStyles();
  const isDisabled = filteredRepCodes.length === 0 || !selectedCustodianId;
  useEffect(() => {
    if (!currentHouseholdRepCode) {
      const selectedRepCode = bdRepCodes.find((rc) => rc.id === bdRepCodeId);
      if (selectedRepCode) {
        onChange(selectedRepCode);
      }
    }
  }, [bdRepCodeId]);

  useEffect(() => {
    if (selectedCustodian && !currentHouseholdRepCode) {
      if (selectedCustodian.requiresCustodialRepCode) {
        const repCodes = bdRepCodes.filter(
          (repcode) => repcode.custodialRepCode !== null
        );
        onChange(null)
        setFilteredRepCodes(repCodes);
      } else {
        if (!currentHouseholdRepCode) {
          onChange(null)
          setFilteredRepCodes(bdRepCodes);
        }
      }
    }
    if (!isDisabled) {
      setBdRepCodeId(null);
    }
  }, [selectedCustodianId]);

  useEffect(() => {
    if (!currentHouseholdRepCode && filteredRepCodes.length === 0) {
      onChange(null)
    }
  }, [filteredRepCodes])

  return (
    <React.Fragment>
      {currentHouseholdRepCode ? (
        <div>
          <BaseTooltip
            labelComponent={
              <React.Fragment>
                Rep Code
              </React.Fragment>}
            helperText="This household is tied to this Rep Code. A new household is needed if you wish to use a different Rep Code."
            placement="right"
            variant="label"
          />
          <Typography variant="body2">
            {currentHouseholdRepCode.bdRepCode} {currentHouseholdRepCode.custodialRepCode ? `/ ${currentHouseholdRepCode.custodialRepCode}` : ''}
          </Typography>
        </div>
      ) : (<TextField
        disabled={isDisabled}
        select
        label={"Rep Code"}
        value={bdRepCodeId}
        onChange={(evt) => setBdRepCodeId(evt.target.value)}
        helperText={
          filteredRepCodes.length === 0
            ? "Please setup your rep codes in Settings."
            : "Please select the Rep Code"
        }
        className={isDisabled && classes.bdRepCodeLabel}
        InputLabelProps={{shrink: true}}
      >
        {filteredRepCodes.map((repCode) => (
          <MenuItem key={repCode.id} value={repCode.id}>
            {repCode.custodialRepCode && selectedCustodian && selectedCustodian.requiresCustodialRepCode
              ? `${repCode.repCode} / ${repCode.custodialRepCode}`
              : `${repCode.repCode}`}
          </MenuItem>
        ))}
      </TextField>)}
    </React.Fragment>
  );
};

export const PershingTPA = ({ onChange }) => {
  const [tpa, setTpa] = useState("no");
  useEffect(() => {
    onChange(tpa === "yes");
  }, [tpa]);
  return (
    <FormControl component="fieldset" margin={"dense"}>
      <FormLabel component="legend" focused={false} style={{ lineHeight: 1.5 }}>
        Is this ERISA plan already established with a Third Party Administrator
        in place?
      </FormLabel>
      <RadioGroup row value={tpa} onChange={(evt) => setTpa(evt.target.value)}>
        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
        <FormControlLabel value="no" control={<Radio />} label="No" />
      </RadioGroup>
    </FormControl>
  );
};


export const CustodialPrimaryAccountHolder = ({ onChange }) => {
  const [accountHolderFirstName, setAccountHolderFirstName] = useState("");
  const [accountHolderLastName, setAccountHolderLastName] = useState("");
  useEffect(() => {
    onChange({
      accountHolderFirstName,
      accountHolderLastName,
      accountHolderName: `${accountHolderFirstName} ${accountHolderLastName}`
    })
  }, [accountHolderFirstName, accountHolderLastName]);

  return (
    <FormControl component="fieldset" margin={"dense"}>
      <FormLabel component="legend" focused={false} style={{marginBottom: 8}}>
        What is the name of the minor?
      </FormLabel>
      <TextField
        label="First Name"
        value={accountHolderFirstName}
        onChange={(evt) => setAccountHolderFirstName(evt.target.value)}
        style={{paddingRight: 8}}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="Last Name"
        value={accountHolderLastName}
        onChange={(evt) => setAccountHolderLastName(evt.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    </FormControl>
  )
};


// WEBPACK FOOTER //
// ./src/components/OnboardPortfoliosDialog/components/Fields/index.jsx